import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  forwardRef,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormsModule,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';
import {
  NgbDateStruct,
  NgbDatepickerModule,
  NgbInputDatepicker,
} from '@ng-bootstrap/ng-bootstrap';
import {
  datesToNgbRange,
  ngbRangeToDates,
} from '@doc/utils';

@Component({
  selector: 'doc-ngb-date-range',
  standalone: true,
  imports: [CommonModule, FormsModule, NgbDatepickerModule],
  template: `
    <div class="doc-ngb-date-range" [ngClass]="containerClass">
      <div class="date-field">
        <input
          #startInput
          class="form-control"
          [ngClass]="inputClass"
          [placeholder]="startPlaceholder"
          [disabled]="disabled"
          [readonly]="readonly"
          ngbDatepicker
          #startPicker="ngbDatepicker"
          [container]="$any(container)"
          [(ngModel)]="startValue"
          (ngModelChange)="onRangeChange()"
          (dateSelect)="onRangeChange()"
          (click)="openStart()" />
        @if (showIcon) {
          <button
            type="button"
            class="input-icon-btn"
            (click)="openStart(); $event.preventDefault(); $event.stopPropagation()"
            [disabled]="disabled"
            aria-label="Open start date picker">
            <i class="pi pi-calendar"></i>
          </button>
        }
      </div>

      <span class="range-separator">{{ separator }}</span>

      <div class="date-field">
        <input
          #endInput
          class="form-control"
          [ngClass]="inputClass"
          [placeholder]="endPlaceholder"
          [disabled]="disabled"
          [readonly]="readonly"
          ngbDatepicker
          #endPicker="ngbDatepicker"
          [container]="$any(container)"
          [(ngModel)]="endValue"
          (ngModelChange)="onRangeChange()"
          (dateSelect)="onRangeChange()"
          [minDate]="$any(startValue)"
          (click)="openEnd()" />
        @if (showIcon) {
          <button
            type="button"
            class="input-icon-btn"
            (click)="openEnd(); $event.preventDefault(); $event.stopPropagation()"
            [disabled]="disabled"
            aria-label="Open end date picker">
            <i class="pi pi-calendar"></i>
          </button>
        }
      </div>
    </div>
  `,
  styleUrl: './doc-ngb-date-range.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DocNgbDateRangeComponent),
      multi: true,
    },
  ],
})
export class DocNgbDateRangeComponent implements ControlValueAccessor {
  @Input() containerClass = '';
  @Input() inputClass = '';
  @Input() startPlaceholder = 'Start date';
  @Input() endPlaceholder = 'End date';
  @Input() separator = '–';
  @Input() readonly = false;
  @Input() showIcon = false;
  @Input() container: 'body' | null = 'body';

  @Input('disabled')
  set disabledInput(value: boolean | string | null | undefined) {
    this.disabled = value === true || value === '';
  }

  @Output() rangeComplete = new EventEmitter<Date[]>();

  @ViewChild('startPicker') private startPicker?: NgbInputDatepicker;
  @ViewChild('endPicker') private endPicker?: NgbInputDatepicker;

  startValue: NgbDateStruct | null = null;
  endValue: NgbDateStruct | null = null;
  disabled = false;

  private onChange: (value: Date[]) => void = () => undefined;
  private onTouched: () => void = () => undefined;

  writeValue(value: Date[] | null): void {
    const range = datesToNgbRange(value);
    this.startValue = range.start;
    this.endValue = range.end;
  }

  registerOnChange(fn: (value: Date[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  onRangeChange(): void {
    const dates = ngbRangeToDates(this.startValue, this.endValue);
    this.onChange(dates);
    this.onTouched();

    if (dates.length === 2) {
      this.rangeComplete.emit(dates);
    }
  }

  openStart(): void {
    if (this.disabled) {
      return;
    }
    this.endPicker?.close();
    this.startPicker?.open();
  }

  openEnd(): void {
    if (this.disabled) {
      return;
    }
    this.startPicker?.close();
    this.endPicker?.open();
  }

  toggleStart(): void {
    if (this.disabled) {
      return;
    }
    this.endPicker?.close();
    this.startPicker?.toggle();
  }

  close(): void {
    this.startPicker?.close();
    this.endPicker?.close();
  }
}
